<template>
  <div class="bookmark-manager pa-4">
    <div class="d-flex align-center mb-6">
      <v-btn
        icon
        variant="text"
        class="mr-2 rounded-lg"
        @click="isSidebarCollapsed = !isSidebarCollapsed"
        v-if="$vuetify.display.mdAndUp"
        :title="isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
      >
        <v-icon :icon="isSidebarCollapsed ? 'mdi-menu' : 'mdi-backburger'"></v-icon>
      </v-btn>
      <v-icon icon="mdi-bookmark-multiple" color="primary" size="large" class="mr-3"></v-icon>
      <h2 class="text-h4 font-weight-bold">{{ isOwnBookmarks ? 'My Bookmarks' : 'Bookmarks' }}</h2>
      <v-spacer></v-spacer>
      <v-btn
        v-if="isOwnBookmarks"
        prepend-icon="mdi-plus"
        color="primary"
        variant="flat"
        class="rounded-lg px-4"
        @click="showCreateDialog = true"
      >
        <span v-if="!isSidebarCollapsed || !$vuetify.display.mdAndUp">New Group</span>
      </v-btn>
    </div>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <div class="mt-4 text-grey">{{ isOwnBookmarks ? 'Loading your collection...' : 'Loading bookmarks...' }}</div>
    </div>

    <div v-else-if="groups.length === 0" class="text-center py-10">
      <v-icon icon="mdi-bookmark-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
      <h3 class="text-h5 text-grey">{{ isOwnBookmarks ? 'Your bookmark list is empty' : "This person doesn't have bookmarks yet" }}</h3>
      <p v-if="isOwnBookmarks" class="text-body-2 text-grey-darken-1">Group posts together to keep track of interesting gamedev topics.</p>
    </div>

    <div v-else class="bookmark-layout-container">
      <div 
        :class="['sidebar-container', { 'collapsed': isSidebarCollapsed && !isHovered && $vuetify.display.mdAndUp }]"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
      >
        <v-list class="floating-list rounded-xl border border-white-10 bg-black-20" density="compact">
          <v-list-item
            v-for="group in groups"
            :key="group.id"
            :active="selectedGroupId === group.id"
            color="primary"
            class="mb-1 rounded-lg px-4"
            @click="selectedGroupId = group.id"
          >
            <template v-slot:prepend>
              <v-avatar
                color="primary"
                variant="tonal"
                size="32"
                class="mr-2"
              >
                <span class="text-caption font-weight-bold">
                  {{ group.name.substring(0, 2).toUpperCase() }}
                </span>
              </v-avatar>
            </template>
            <v-list-item-title class="font-weight-bold group-name text-truncate">
              {{ group.name }}
            </v-list-item-title>
            <v-list-item-subtitle class="group-count">
              {{ group.postUris.length }} posts
            </v-list-item-subtitle>
            <v-tooltip 
              activator="parent" 
              location="right" 
              offset="15"
              v-if="isSidebarCollapsed && !isHovered"
            >
              {{ group.name }}
            </v-tooltip>
          </v-list-item>
        </v-list>
      </div>

      <div class="content-container flex-grow-1 min-width-0">
        <div v-if="!selectedGroup" class="text-center py-10 border border-white-05 rounded-xl bg-black-05">
           <p class="text-grey">Select a group to see its posts</p>
        </div>
        <div v-else>
          <div class="d-flex align-center mb-4 px-2">
            <h3 class="text-h5 font-weight-bold">{{ selectedGroup.name }}</h3>
            <v-chip class="ml-3" size="small" color="grey">{{ selectedPosts.length }} posts</v-chip>
            
            <v-spacer></v-spacer>

            <v-btn v-if="isOwnBookmarks" icon variant="text" size="small">
              <v-icon icon="mdi-dots-vertical"></v-icon>
              <v-menu activator="parent" transition="slide-y-transition" location="bottom end">
                <v-list density="compact" class="rounded-lg shadow-lg">
                  <v-list-item
                    prepend-icon="mdi-pencil"
                    title="Rename Group"
                    @click="promptRename(selectedGroup)"
                  ></v-list-item>
                  <v-list-item
                    prepend-icon="mdi-delete"
                    title="Delete Group"
                    base-color="error"
                    @click="deleteGroup(selectedGroupId!)"
                  ></v-list-item>
                </v-list>
              </v-menu>
            </v-btn>
          </div>

          <div v-if="fetchingPosts" class="text-center py-10">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="selectedPosts.length === 0" class="text-center py-10 text-grey border border-white-05 rounded-xl dashed">
            This group has no posts yet.
          </div>
          <v-fade-transition group v-else>
            <PostItem
              v-for="post in selectedPosts"
              :key="post.uri"
              :post="post"
              :show-remove-bookmark="isOwnBookmarks"
              @view-thread="$emit('view-thread', post)"
              @delete="$emit('delete', post)"
              @tag-click="$emit('tag-click', $event)"
              @view-profile="$emit('view-profile', $event)"
              @remove-bookmark="removePostFromGroup"
            />
          </v-fade-transition>
        </div>
      </div>
    </div>

    <!-- Create Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="400">
      <v-card class="rounded-xl border border-white-05 bg-surface-variant backdrop-blur text-white">
        <v-card-title class="pa-4 font-weight-bold">Create New Group</v-card-title>
        <v-card-text class="pa-4 pt-0">
          <v-text-field
            v-model="newGroupName"
            placeholder="e.g. Art Inspiration, Useful Tools"
            variant="outlined"
            density="compact"
            hide-details
            class="rounded-lg"
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showCreateDialog = false; newGroupName = ''">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :disabled="!newGroupName.trim()" @click="createGroup" class="rounded-lg px-6">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rename Dialog -->
    <v-dialog v-model="showRenameDialog" max-width="400">
      <v-card class="rounded-xl border border-white-05 bg-surface-variant backdrop-blur text-white">
        <v-card-title class="pa-4 font-weight-bold">Rename Group</v-card-title>
        <v-card-text class="pa-4 pt-0">
          <v-text-field
            v-model="renameValue"
            variant="outlined"
            density="compact"
            hide-details
            class="rounded-lg"
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showRenameDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :disabled="!renameValue.trim()" @click="renameGroup" class="rounded-lg px-6">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card class="rounded-xl border border-white-05 bg-surface-variant backdrop-blur text-white">
        <v-card-title class="pa-4 font-weight-bold">Delete Group?</v-card-title>
        <v-card-text class="pa-4 pt-0">
          Are you sure you want to delete this bookmark group? The posts themselves will not be deleted.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete" class="rounded-lg px-6">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { getBookmarks, saveBookmarks, bookmarkGroups, getPostsBatch, getCurrentUser, showAlert } from '@/services/bluesky';
import PostItem from '@/components/PostItem.vue';

const props = defineProps<{
  userDid?: string
}>();

const emit = defineEmits(['view-thread', 'delete', 'tag-click', 'view-profile']);

const loading = ref(true);
const fetchingPosts = ref(false);
const groups = ref<any[]>([]);
const selectedGroupId = ref<string | null>(null);
const selectedPosts = ref<any[]>([]);
const isSidebarCollapsed = ref(true); // Default to collapsed for hover-to-expand UX
const isHovered = ref(false);

const user = getCurrentUser();
const isOwnBookmarks = computed(() => {
  if (!props.userDid) return true;
  return !!(user && props.userDid === user.did);
});

const showCreateDialog = ref(false);
const newGroupName = ref('');

const showRenameDialog = ref(false);
const renameValue = ref('');
const renamingGroup = ref<any>(null);

const showDeleteDialog = ref(false);
const deletingGroupId = ref<string | null>(null);

const selectedGroup = computed(() => 
  groups.value.find(g => g.id === selectedGroupId.value)
);

const fetchBookmarks = async () => {
  loading.value = true;
  const bookmarks = await getBookmarks(props.userDid);
  if (bookmarks) {
    groups.value = bookmarks.groups || [];
    if (groups.value.length > 0 && !selectedGroupId.value) {
      selectedGroupId.value = groups.value[0].id;
    }
  }
  loading.value = false;
};

const fetchGroupPosts = async () => {
  if (!selectedGroup.value) {
    selectedPosts.value = [];
    return;
  }
  
  fetchingPosts.value = true;
  const posts = await getPostsBatch(selectedGroup.value.postUris);
  
  // Cross-reference to find missing posts
  const fetchedUris = new Set(posts.map((p: any) => p.uri));
  const missingPosts = selectedGroup.value.postUris
    .filter((uri: string) => !fetchedUris.has(uri))
    .map((uri: string) => ({ 
      uri, 
      unavailable: true,
      author: { handle: 'unknown' }, // Minimal stub for PostItem baseline
      record: { text: '' }
    }));
    
  selectedPosts.value = [...posts, ...missingPosts];
  fetchingPosts.value = false;
};

const createGroup = async () => {
  const name = newGroupName.value.trim();
  if (!name) return;
  
  const newId = Math.random().toString(36).substring(2, 11);
  const updatedGroups = [...groups.value, {
    id: newId,
    name: name,
    postUris: []
  }];
  
  await updateAndSave(updatedGroups);
  selectedGroupId.value = newId;
  showCreateDialog.value = false;
  newGroupName.value = '';
};

const promptRename = (group: any) => {
  renamingGroup.value = group;
  renameValue.value = group.name;
  showRenameDialog.value = true;
};

const renameGroup = async () => {
  if (!renameValue.value.trim() || !renamingGroup.value) return;
  
  const updatedGroups = groups.value.map(g => 
    g.id === renamingGroup.value.id ? { ...g, name: renameValue.value.trim() } : g
  );
  
  await updateAndSave(updatedGroups);
  showRenameDialog.value = false;
};

const deleteGroup = (id: string) => {
  deletingGroupId.value = id;
  showDeleteDialog.value = true;
};

const confirmDelete = async () => {
  if (!deletingGroupId.value) return;
  
  const id = deletingGroupId.value;
  const updatedGroups = groups.value.filter(g => g.id !== id);
  await updateAndSave(updatedGroups);
  
  if (selectedGroupId.value === id) {
    selectedGroupId.value = groups.value.length > 0 ? groups.value[0].id : null;
  }
  
  showDeleteDialog.value = false;
  deletingGroupId.value = null;
};

const removePostFromGroup = async (postUri: string) => {
  if (!selectedGroup.value) return;
  
  const updatedGroups = groups.value.map(g => {
    if (g.id === selectedGroupId.value) {
      return { ...g, postUris: g.postUris.filter((uri: string) => uri !== postUri) };
    }
    return g;
  });
  
  await updateAndSave(updatedGroups);
  await fetchGroupPosts(); // Refresh view
};

const updateAndSave = async (updatedGroups: any[]) => {
  try {
    await saveBookmarks({ groups: updatedGroups });
    groups.value = updatedGroups;
  } catch (e) {
    await showAlert('Failed to save changes');
  }
};

watch(selectedGroupId, () => {
  fetchGroupPosts();
});

watch(bookmarkGroups, (newGroups) => {
  if (isOwnBookmarks.value) {
    groups.value = newGroups;
  }
}, { deep: true });

onMounted(() => {
  fetchBookmarks();
});
</script>

<style scoped>
.bookmark-manager {
  max-width: 1400px;
  margin: 0 auto;
}
.bg-black-05 {
  background: rgba(0, 0, 0, 0.2);
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
.dashed {
  border-style: dashed !important;
}
.backdrop-blur {
  backdrop-filter: blur(20px);
}

.bookmark-layout-container {
  position: relative;
  display: flex;
}

.sidebar-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 300px;
  z-index: 100;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  overflow: hidden;
}

.sidebar-container .floating-list {
  pointer-events: auto;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(40px) saturate(180%);
  overflow-x: hidden;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.sidebar-container.collapsed {
  width: 64px;
}

.sidebar-container.collapsed :deep(.v-list-item) {
  padding-left: 12px !important;
  padding-right: 12px !important;
}

.sidebar-container.collapsed .group-name,
.sidebar-container.collapsed .group-count {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
}

.content-container {
  width: 100%;
  padding-left: 84px; /* Space for the collapsed rail */
  transition: padding-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}


.bg-black-20 {
  background: rgba(0, 0, 0, 0.4) !important;
}

.border-white-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

@media (max-width: 960px) {
  .bookmark-layout-container {
    flex-direction: column;
  }
  .sidebar-container {
    position: relative;
    width: 100% !important;
    margin-bottom: 24px;
  }
  .content-container {
    padding-left: 0 !important;
  }
}

.min-width-0 {
  min-width: 0;
}
</style>
